import React, { useState } from "react";
import { Sparkle as SparkleIcon } from "@phosphor-icons/react";
import { Tooltip, CircularProgress } from "@mui/material";
import { create } from "zustand";
import { triggerAutofillForAttribute, triggerAutofillBulk } from "../api";
import { Doorway } from "../../design/Doorway";

// Issue #4914: on-demand AI autofill triggers for the Facts tab.
//
// The entry points share an API helper (_triggerAutofill in
// api.ts); this file renders the three UI affordances:
//   - InlineFieldAutofillControl: per-field sparkle rendered to the left
//     of an unset input. Reads from the shared autofill status store
//     which is updated by the RmsEntity websocket handler on the
//     EntityDashboard — no polling.
//   - FactAutofillOffer: the same per-field action in the Facts page's
//     read mode, in the empty value slot (#12035).
//   - BulkAutofillToolbarAction: selected-rows action (wired by the
//     caller that owns the selection state).

export type InlineAutofillStatus = "idle" | "pending" | "not_found" | "error";

interface AutofillStatusStore {
  // attribute_id -> current ephemeral status for the inline chip. Idle
  // by default (not in the map). Updated by the RMS-entity websocket
  // handler when the autofill_query_task reports its outcome, and by
  // the chip itself when the user fires a trigger.
  statuses: Record<number, InlineAutofillStatus>;
  // attribute_id set flagged by the websocket handler when an autofill
  // just wrote a new value. Consumed (cleared) by the fact renderer on
  // its fresh mount to drive a one-shot typewriter reveal of the text.
  justFilledIds: Set<number>;
  setStatus: (attributeId: number, status: InlineAutofillStatus) => void;
  clearStatus: (attributeId: number) => void;
  // Re-derive the pending chips from the entity GET's `autofill_pending`
  // flags. The websocket is fire-and-forget, so a terminal event pushed
  // while the socket was down never arrives and the chip spins until the
  // user reloads; the dashboard calls this whenever entity data refreshes
  // (including on reconnect) to resettle against the server.
  //
  // The entity GET reports every fact, so an attribute absent from
  // `pendingAttributeIds` is known not to be in flight. Only `pending` is
  // server-owned — `not_found` / `error` were already delivered and say
  // something the in-flight set does not, so a sync leaves them alone,
  // unless the server reports that attribute in flight again (a newer run
  // was dispatched, so the stale terminal chip is the wrong thing to show).
  syncPending: (pendingAttributeIds: number[]) => void;
  // What the record said about the attribute when this page dispatched a
  // run for it, keyed by attribute: the previous run's timestamp
  // (`autofill_last_outcome_at` off the fact the dispatching control is
  // rendering; null when no run is on record) and when the click happened.
  // The run's own pending row lands two Celery hops after the POST returns,
  // so until the record's timestamp moves past this baseline the record
  // still describes the previous run and must not be read as this one's
  // ending. A server-confirmed pending (`confirmPending`) or any terminal
  // status clears the baseline. The baseline is taken from the fact at the
  // call site rather than mirrored in the store: a mirror has to be seeded
  // before the first click and survive the per-record reset, and the one
  // this had was wiped on every warm-cache mount (#12035).
  dispatchBaselines: Record<
    number,
    { outcomeAt: string | null; dispatchedAt: number }
  >;
  // Mark a run this page started: pending, with its baseline.
  markDispatched: (attributeId: number, lastOutcomeAt: string | null) => void;
  // Mark a run the server reports in flight (its pending row exists).
  confirmPending: (attributeId: number) => void;
  // Drops every cached status + just-filled flag. Called on entity
  // navigation so a "not_found" or "error" attached to attribute X on
  // Entity A doesn't render as the chip state for attribute X on
  // Entity B (attribute ids repeat across entities of the same kind).
  resetAll: () => void;
  markJustFilled: (attributeId: number) => void;
  consumeJustFilled: (attributeId: number) => boolean;
}

const withoutBaseline = (
  baselines: AutofillStatusStore["dispatchBaselines"],
  attributeId: number
): AutofillStatusStore["dispatchBaselines"] => {
  if (!(attributeId in baselines)) return baselines;
  const next = { ...baselines };
  delete next[attributeId];
  return next;
};

export const useAutofillStatusStore = create<AutofillStatusStore>(
  (set, get) => ({
    statuses: {},
    justFilledIds: new Set<number>(),
    dispatchBaselines: {},
    setStatus: (attributeId, status) =>
      set((state) => ({
        statuses: { ...state.statuses, [attributeId]: status },
        // A terminal status is how the run ended; nothing is waited on.
        dispatchBaselines:
          status === "pending"
            ? state.dispatchBaselines
            : withoutBaseline(state.dispatchBaselines, attributeId),
      })),
    clearStatus: (attributeId) =>
      set((state) => {
        const next = { ...state.statuses };
        delete next[attributeId];
        return {
          statuses: next,
          dispatchBaselines: withoutBaseline(
            state.dispatchBaselines,
            attributeId
          ),
        };
      }),
    syncPending: (pendingAttributeIds) =>
      set((state) => {
        const pending = new Set(pendingAttributeIds);
        const next: Record<number, InlineAutofillStatus> = {};
        let baselines = state.dispatchBaselines;
        for (const [key, status] of Object.entries(state.statuses)) {
          const attributeId = Number(key);
          if (status === "pending" && !pending.has(attributeId)) {
            // A pending the server no longer reports has ended (the value
            // arrived with the refetch, or nothing did); its baseline is
            // spent like any other terminal transition's.
            baselines = withoutBaseline(baselines, attributeId);
            continue;
          }
          next[attributeId] = status;
        }
        for (const attributeId of pending) {
          next[attributeId] = "pending";
        }
        return { statuses: next, dispatchBaselines: baselines };
      }),
    markDispatched: (attributeId, lastOutcomeAt) =>
      set((state) => ({
        statuses: { ...state.statuses, [attributeId]: "pending" },
        dispatchBaselines: {
          ...state.dispatchBaselines,
          [attributeId]: { outcomeAt: lastOutcomeAt, dispatchedAt: Date.now() },
        },
      })),
    confirmPending: (attributeId) =>
      set((state) => ({
        statuses: { ...state.statuses, [attributeId]: "pending" },
        dispatchBaselines: withoutBaseline(
          state.dispatchBaselines,
          attributeId
        ),
      })),
    resetAll: () =>
      set({
        statuses: {},
        justFilledIds: new Set<number>(),
        dispatchBaselines: {},
      }),
    markJustFilled: (attributeId) =>
      set((state) => {
        const next = new Set(state.justFilledIds);
        next.add(attributeId);
        return { justFilledIds: next };
      }),
    consumeJustFilled: (attributeId) => {
      const current = get().justFilledIds;
      if (!current.has(attributeId)) return false;
      const next = new Set(current);
      next.delete(attributeId);
      set({ justFilledIds: next });
      return true;
    },
  })
);

interface InlineFieldAutofillControlProps {
  publicEntityId: string;
  attributeId: number;
  // The fact's `autofill_last_outcome_at` as rendered: the dispatch baseline.
  lastOutcomeAt: string | null;
}

// Sparkle chip rendered inside an unset field's input as a leading
// adornment (see EditableFactRenderer's renderInputPrefix slot). Click
// kicks off an autofill, then waits for the RMS-entity websocket event
// to flip the status (filled → reset, not_found / error → red with a
// retry tooltip). State lives in the shared useAutofillStatusStore so
// cadence and bulk writes can update the same chip without polling.
// One attribute's autofill status and the click that starts a run, shared by
// the editor chip and the read-mode offer so both render the same store
// entry and dispatch the same way. Marks the attribute pending at once; the
// outcome arrives over the record websocket, whose handler on
// EntityDashboard writes the final status. A click while a run is already
// pending is ignored (the chip stays mounted, disabled, through the run).
const useAutofillDispatch = (
  publicEntityId: string,
  attributeId: number,
  lastOutcomeAt: string | null
) => {
  const status =
    useAutofillStatusStore((s) => s.statuses[attributeId]) ?? "idle";
  const setStatus = useAutofillStatusStore((s) => s.setStatus);
  const markDispatched = useAutofillStatusStore((s) => s.markDispatched);
  const dispatch = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (status === "pending") return;
    markDispatched(attributeId, lastOutcomeAt);
    try {
      await triggerAutofillForAttribute(publicEntityId, attributeId);
    } catch (err) {
      console.error("Field autofill trigger failed:", err);
      setStatus(attributeId, "error");
    }
  };
  return { status, dispatch };
};

export const InlineFieldAutofillControl: React.FC<
  InlineFieldAutofillControlProps
> = ({ publicEntityId, attributeId, lastOutcomeAt }) => {
  const { status, dispatch: onClick } = useAutofillDispatch(
    publicEntityId,
    attributeId,
    lastOutcomeAt
  );

  const isError = status === "not_found" || status === "error";
  const tooltipTitle =
    status === "not_found"
      ? "Answer not found"
      : status === "error"
        ? "Autofill failed. Click to retry."
        : "";
  const chipClassName = isError
    ? "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-red-600"
    : "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-[#2A3DCB] bg-[#F7F8FF] hover:bg-[#EEF0FF] disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#2A3DCB]";

  const chip = (
    <button
      type="button"
      onClick={onClick}
      disabled={status === "pending"}
      className={chipClassName}
      aria-label="Autofill this field with AI"
    >
      {status === "pending" ? (
        <CircularProgress
          size={12}
          thickness={5}
          sx={{ color: isError ? "#B91C1C" : "#2A3DCB" }}
        />
      ) : (
        <SparkleIcon size={12} weight="fill" />
      )}
      <span>Autofill</span>
    </button>
  );

  if (!tooltipTitle) return chip;
  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      {chip}
    </Tooltip>
  );
};

interface BulkAutofillToolbarActionProps {
  publicEntityId: string;
  // Each selected attribute with its fact's `autofill_last_outcome_at`, the
  // dispatch baseline for that field.
  attributes: { id: number; lastOutcomeAt: string | null }[];
  onDispatched?: (workspaceId: string) => void;
}

export const BulkAutofillToolbarAction: React.FC<
  BulkAutofillToolbarActionProps
> = ({ publicEntityId, attributes, onDispatched }) => {
  const attributeIds = attributes.map((attribute) => attribute.id);
  const [pending, setPending] = useState(false);
  const setStatus = useAutofillStatusStore((s) => s.setStatus);
  const markDispatched = useAutofillStatusStore((s) => s.markDispatched);

  const onClick = async () => {
    if (pending || attributeIds.length === 0) return;
    setPending(true);
    // Optimistically mark every selected field as pending so each chip
    // shows the spinner while the bulk task dispatches. Outcomes arrive
    // individually via websocket, or by the record page settling against
    // the record (each mark carries its dispatch baseline).
    attributes.forEach((attribute) =>
      markDispatched(attribute.id, attribute.lastOutcomeAt)
    );
    try {
      const resp = await triggerAutofillBulk(publicEntityId, attributeIds);
      onDispatched?.(resp.workspace_id);
    } catch (err) {
      console.error("Bulk autofill trigger failed:", err);
      attributeIds.forEach((aid) => setStatus(aid, "error"));
    } finally {
      setPending(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending || attributeIds.length === 0}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-[#2A3DCB] bg-[#F7F8FF] hover:bg-[#EEF0FF] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#2A3DCB]"
    >
      {pending ? (
        <CircularProgress size={14} thickness={5} sx={{ color: "#2A3DCB" }} />
      ) : (
        <SparkleIcon size={14} weight="fill" />
      )}
      <span>Autofill selected with AI ({attributeIds.length})</span>
    </button>
  );
};

interface FactAutofillOfferProps {
  publicEntityId: string;
  attributeId: number;
  // The field's display name, so the control names its field the way the
  // row's pencil does ("Edit Legal Structure").
  fieldName: string;
  // The fact's `autofill_last_outcome_at` as rendered: the dispatch baseline.
  lastOutcomeAt: string | null;
}

// The read-mode offer on the Facts page (#12035). The redesigned page reads
// at rest and only mounts the editor (and its chip above) once a row is
// opened, so an unfilled AI-enabled fact read as a bare dash with no way to
// ask for it. Per the Claude Design ruling in
// `.claude/features/12035-facts-autofill-affordance.md`, the empty value slot
// itself carries the offer, on the record grammar's tokens rather than the
// chip's pill: a quiet "✦ Autofill" at rest (ink-3, accent on hover),
// "✦ Filling…" in ink-4 with a 12px spinner in flight, and after a run that
// produced nothing the outcome in words with a text-control retry. A write
// to the record is visible at rest, never hover-revealed. Shares the status
// store with the chip, so the record websocket drives both the same way.
export const FactAutofillOffer: React.FC<FactAutofillOfferProps> = ({
  publicEntityId,
  attributeId,
  fieldName,
  lastOutcomeAt,
}) => {
  const { status, dispatch } = useAutofillDispatch(
    publicEntityId,
    attributeId,
    lastOutcomeAt
  );

  const label: React.CSSProperties = {
    fontFamily: "var(--fp-font-ui)",
    fontSize: "var(--fp-t-meta)",
    fontWeight: "var(--fp-w-primary)" as unknown as number,
  };
  // The spinner's slot is held at rest so the switch to in-flight moves
  // nothing. It trails the label so the glyph sits where every other value
  // in the column starts.
  const spinnerSlot: React.CSSProperties = {
    display: "inline-grid",
    placeItems: "center",
    width: 12,
    height: 12,
    flex: "none",
  };

  if (status === "pending") {
    return (
      <span
        role="status"
        style={{
          ...label,
          display: "inline-flex",
          alignItems: "center",
          gap: "var(--fp-gap-hair)",
          color: "var(--fp-ink-4)",
        }}
      >
        <span>✦ Filling…</span>
        <span style={spinnerSlot}>
          <CircularProgress
            size={12}
            thickness={5}
            sx={{ color: "var(--fp-ink-4)" }}
          />
        </span>
      </span>
    );
  }

  if (status === "not_found" || status === "error") {
    const failed = status === "error";
    return (
      // The outcome of the run is the one thing worth announcing, so the
      // live region sits on the terminal state, not on the spinner.
      <span
        role="status"
        style={{
          ...label,
          display: "inline-flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--fp-gap-hair) var(--fp-gap-tight)",
        }}
      >
        <span style={{ color: failed ? "var(--fp-neg)" : "var(--fp-ink-4)" }}>
          {failed ? "Autofill failed" : "Answer not found"}
        </span>
        <Doorway onClick={() => void dispatch()}>
          {failed ? "Retry" : "Try again"}
        </Doorway>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={dispatch}
      aria-label={`Autofill ${fieldName} with AI`}
      // The glyph is part of the label here, not provenance yet, so it takes
      // the label's colour rather than staying ink-4.
      className="text-[var(--fp-ink-3)] hover:text-[var(--fp-accent)] focus-visible:text-[var(--fp-accent)]"
      style={{
        ...label,
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--fp-gap-hair)",
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      <span>✦ Autofill</span>
      <span style={spinnerSlot} aria-hidden="true" />
    </button>
  );
};
